import React from "react";
import { Loader2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export function Spinner({ className = "" }) {
  return <Loader2 className={`h-5 w-5 animate-spin text-cyanx ${className}`} />;
}

export function LoadingBlock({ label }) {
  const { lang } = useI18n();
  const text = label || (lang === "en" ? "Loading…" : lang === "de" ? "Wird geladen…" : "Caricamento…");
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-slate-500" data-testid="loading-block">
      <Loader2 className="h-5 w-5 animate-spin text-cyanx" />{text}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 bg-navy-paper p-10 text-center" data-testid="empty-state">
      {Icon && <Icon className="mx-auto h-8 w-8 text-slate-600" />}
      <h3 className="mt-3 font-heading text-lg text-white">{title}</h3>
      {description && <p className="mt-1.5 text-sm text-slate-400 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, tone = "cyan", testid }) {
  const tones = {
    cyan: "text-cyanx bg-cyanx/10 border-cyanx/20",
    gold: "text-gold bg-gold/10 border-gold/20",
    green: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    red: "text-red-400 bg-red-500/10 border-red-500/20",
    slate: "text-slate-300 bg-white/5 border-white/10",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-navy-paper p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${tones[tone]}`}><Icon className="h-4 w-4" /></span>}
      </div>
      <div className="mt-3 font-heading text-3xl font-light text-white">{value}</div>
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return <div className={`rounded-xl border border-white/10 bg-navy-paper ${className}`} {...props}>{children}</div>;
}

const LOCALE_MAP = { de: "de-DE", it: "it-IT", en: "en-GB" };
function activeLocale() {
  try { return LOCALE_MAP[localStorage.getItem("rc_lang")] || "de-DE"; } catch { return "de-DE"; }
}
export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(activeLocale(), { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}
export function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(activeLocale(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
export function formatCurrency(amount, currency = "EUR") {
  if (amount == null) return "—";
  try { return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(amount); }
  catch { return `${amount} ${currency}`; }
}
